import { ComponentProps, useEffect, useRef, useState } from "react";
import {CommunityType} from "@/pages/communities/types.ts";
import { Form as InertiaForm } from '@inertiajs/react'
import {Label} from "@/components/ui/label.tsx";
import {Input} from "@/components/ui/input.tsx";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/components/ui/select.tsx";
import {Button} from "@/components/ui/button.tsx";
import LocationMap from "@/pages/communities/components/location-map.tsx";


type FormProps = Omit<ComponentProps<typeof InertiaForm>, 'children'> & {
    community: CommunityType
    submitText: string
}

interface AdresseSuggestion {
    display_name: string
    lat: string
    lon: string
}

const statusOptions = [
    { value: 'active', label: 'Actif' },
    { value: 'inactive', label: 'Inactif' },
] as const


export default function Form({ community, submitText, ...formProps }: FormProps) {
    const initialStatus = community.status || "active"
    const [status, setStatus] = useState(initialStatus)
    const [address, setAdresse] = useState(community.address || "")
    const [lat, setLat] = useState(community.lat || "")
    const [lng, setLng] = useState(community.lng || "")
    const [addressSuggestions, setAdresseSuggestions] = useState<AdresseSuggestion[]>([])
    const [searchingAdresse, setSearchingAdresse] = useState(false)
    const [addressError, setAdresseError] = useState("")
    const [recenterMapKey, setRecenterMapKey] = useState(0)
    const skipAutocompleteOnceRef = useRef(false)

    useEffect(() => {
        setStatus(initialStatus)
    }, [initialStatus])

    useEffect(() => {
        if (skipAutocompleteOnceRef.current) {
            skipAutocompleteOnceRef.current = false
            return
        }

        const query = address.trim()
        if (query.length < 3) {
            setAdresseSuggestions([])
            setAdresseError("")
            return
        }

        const controller = new AbortController()
        const timeoutId = window.setTimeout(async () => {
            try {
                setSearchingAdresse(true)
                setAdresseError("")

                const response = await fetch(
                    `/communities/geocode?query=${encodeURIComponent(query)}`,
                    { signal: controller.signal }
                )

                if (!response.ok) {
                    throw new Error("Unable to fetch addresses")
                }

                const results = await response.json() as AdresseSuggestion[]
                setAdresseSuggestions(results)
            } catch (error) {
                if (!controller.signal.aborted) {
                    setAdresseError("Impossible de charger les suggestions d'adresse")
                    setAdresseSuggestions([])
                }
            } finally {
                if (!controller.signal.aborted) {
                    setSearchingAdresse(false)
                }
            }
        }, 300)

        return () => {
            controller.abort()
            window.clearTimeout(timeoutId)
        }
    }, [address])

    const handleAdresseSelect = (suggestion: AdresseSuggestion) => {
        skipAutocompleteOnceRef.current = true
        setAdresse(suggestion.display_name)
        setLat(suggestion.lat)
        setLng(suggestion.lon)
        setRecenterMapKey(previous => previous + 1)
        setAdresseSuggestions([])
        setAdresseError("")
    }

    const handleMapAdresseResolved = (value: string) => {
        skipAutocompleteOnceRef.current = true
        setAdresse(value)
        setAdresseSuggestions([])
        setAdresseError("")
    }

    return (
        <InertiaForm
            encType="multipart/form-data"
            transform={data => ({ community: data })}
            {...formProps}
        >
            {({ errors, processing }) => (
                <div className={"space-y-4"}>
                    <div className="space-y-2">
                        <Label htmlFor="logo">Logo</Label>
                        {community.logo_url && (
                            <div className="flex items-center gap-3 rounded-md border border-border bg-card p-3">
                                <img
                                    src={community.logo_url}
                                    alt={`Logo ${community.name}`}
                                    className="h-16 w-16 rounded-md border border-border object-contain"
                                />
                                <div className="text-sm text-muted-foreground">
                                    Logo actuel
                                </div>
                            </div>
                        )}
                        <Input
                            type="file"
                            name="logo"
                            id="logo"
                            accept="image/png,image/jpeg,image/webp,image/gif"
                        />
                        {errors.logo && (
                            <div className="text-red-500 px-3 py-2 font-medium">
                                {errors.logo.join(', ')}
                            </div>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="name">Nom de la commune</Label>
                        <Input
                            placeholder={"Nom de la commune"}
                            type="text"
                            name="name"
                            id="name"
                            defaultValue={community.name}
                        />
                        {errors.name && (
                            <div className="text-red-500 px-3 py-2 font-medium">
                                {errors.name.join(', ')}
                            </div>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="location">Localité</Label>
                        <Input
                            type="text"
                            name="location"
                            placeholder={"Localité"}
                            id="location"
                            defaultValue={community.location}
                        />
                        {errors.location && (
                            <div className="text-red-500 px-3 py-2 font-medium">
                                {errors.location.join(', ')}
                            </div>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="phone">Numéro de téléphone</Label>
                        <Input
                            type="text"
                            name="phone"
                            id="phone"
                            placeholder={"Numéro de téléphone"}
                            defaultValue={community.phone}
                        />
                        {errors.phone && (
                            <div className="text-red-500 px-3 py-2 font-medium">
                                {errors.phone.join(', ')}
                            </div>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="email">Adresse mail</Label>
                        <Input
                            type="text"
                            name="email"
                            id="email"
                            placeholder={"Adresse mail"}
                            defaultValue={community.email}
                        />
                        {errors.email && (
                            <div className="text-red-500 px-3 py-2 font-medium">
                                {errors.email.join(', ')}
                            </div>
                        )}
                    </div>

                    <LocationMap
                        address={address}
                        lat={lat}
                        lng={lng}
                        recenterKey={recenterMapKey}
                        addressSuggestions={addressSuggestions}
                        searchingAdresse={searchingAdresse}
                        addressError={addressError}
                        fieldErrors={{
                            address: errors.address,
                            lat: errors.lat,
                            lng: errors.lng,
                        }}
                        onAdresseChange={setAdresse}
                        onAdresseSelect={handleAdresseSelect}
                        onLatChange={setLat}
                        onLngChange={setLng}
                        onAdresseResolved={handleMapAdresseResolved}
                    />

                    <div className="space-y-2">
                        <Label htmlFor="status">Statut</Label>
                        <input type="hidden" name="status" value={status} />
                        <Select value={status} onValueChange={setStatus}>
                            <SelectTrigger className="w-full text-foreground">
                                <SelectValue placeholder="Statut" className="text-foreground" />
                            </SelectTrigger>
                            <SelectContent className="text-foreground">
                                {statusOptions.map(option => (
                                    <SelectItem className="text-foreground" key={option.value} value={option.value}>
                                        {option.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {errors.status && (
                            <div className="text-red-500 px-3 py-2 font-medium">
                                {errors.status.join(', ')}
                            </div>
                        )}
                    </div>

                    <div className="inline">
                        <Button
                            type="submit"
                            disabled={processing}
                            className=" cursor-pointer"
                        >
                            {submitText}
                        </Button>
                    </div>
                </div>
            )}
        </InertiaForm>
    )
}
